import type {
	ForecastModel,
	PatternEvidenceModel,
	ReasonModel,
} from '@/shared/generated'

// The wire sends one evidence type per reason code — `code` is the discriminator,
// the payloads are not tellable apart structurally by TS alone.
export type ReasonEvidence =
	| { kind: 'age'; daysLive: number; minDays: number }
	| { kind: 'dormancy'; daysSinceLastSale: number; maxDays: number }
	| {
			kind: 'pattern'
			pattern: PatternEvidenceModel['pattern']
			allowed: PatternEvidenceModel['allowed']
	  }

const UNKNOWN_REASON: ReasonModel = { code: 'unknown', factor: 'common' }

export const normalizeReasons = (forecast: ForecastModel): ReasonModel[] => {
	if (forecast.is_forecastable) return []

	return forecast.reasons?.length ? forecast.reasons : [UNKNOWN_REASON]
}

export const isFactorBlocked = (
	reasons: ReasonModel[],
	factor: ReasonModel['factor']
): boolean => reasons.some((reason) => reason.factor === factor)

export const isDemandBlocked = (reasons: ReasonModel[]): boolean =>
	isFactorBlocked(reasons, 'demand')

export const isLifecycleBlocked = (reasons: ReasonModel[]): boolean =>
	isFactorBlocked(reasons, 'lifecycle')

export const pickEvidence = (reason: ReasonModel): ReasonEvidence | null => {
	const { evidence } = reason

	if (!evidence) return null

	switch (reason.code) {
		case 'is_new':
			return 'days_live' in evidence
				? {
						kind: 'age',
						daysLive: evidence.days_live,
						minDays: evidence.min_days,
					}
				: null
		case 'dormant':
			return 'days_since_last_sale' in evidence
				? {
						kind: 'dormancy',
						daysSinceLastSale: evidence.days_since_last_sale,
						maxDays: evidence.max_days,
					}
				: null
		case 'intermittent':
		case 'lumpy':
			return 'pattern' in evidence
				? {
						kind: 'pattern',
						pattern: evidence.pattern,
						allowed: evidence.allowed,
					}
				: null
		default:
			return null
	}
}
